import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Lock, CreditCard, ShoppingCart } from 'lucide-react';
import { Button } from './ui/Button';
import { OrderBump } from './ui/OrderBump';
import { UpsellPopup } from './UpsellPopup';
import { trackAddToCart } from '../lib/pixel';
// ---- Shopify checkout URLs ----
// Each combination of products is sold as its own Shopify bundle variant.
const CHECKOUT_URLS = {
  base: 'https://noveliahub.com/cart/44193210630180:1',
  baseAndBreakfast: 'https://noveliahub.com/cart/44196581507108:1',
  baseAndNight: 'https://noveliahub.com/cart/44196586455076:1',
  baseAndBoth: 'https://noveliahub.com/cart/44196583866404:1'
};
const BASE_PRICE = 14;
const BREAKFAST_PRICE = 9;
const NIGHT_PRICE = 7;
export function PricingSection() {
  const [bumpBreakfast, setBumpBreakfast] = useState(false);
  const [bumpNight, setBumpNight] = useState(false);
  const [showUpsell, setShowUpsell] = useState(false);
  const anyBumpSelected = bumpBreakfast || bumpNight;
  const total = BASE_PRICE + (bumpBreakfast ? BREAKFAST_PRICE : 0) + (bumpNight ? NIGHT_PRICE : 0);
  // Pick the right Shopify bundle URL based on what's selected
  const checkoutUrl = bumpBreakfast && bumpNight ? CHECKOUT_URLS.baseAndBoth : bumpBreakfast ? CHECKOUT_URLS.baseAndBreakfast : bumpNight ? CHECKOUT_URLS.baseAndNight : CHECKOUT_URLS.base;
  const lineItems = [{
    label: 'Menopause Reset + 3 BONOS',
    price: BASE_PRICE,
    active: true
  }, {
    label: '21 Desayunos Hormonales en 5 Minutos',
    price: BREAKFAST_PRICE,
    active: bumpBreakfast
  }, {
    label: 'Guía de Rescate Nocturno',
    price: NIGHT_PRICE,
    active: bumpNight
  }];
  // Intercept checkout: if no bumps selected, show upsell popup first.
  const handleCheckoutClick = () => {
    if (anyBumpSelected) {
      trackAddToCart();
      window.location.href = checkoutUrl;
    } else {
      setShowUpsell(true);
    }
  };
  const handleUpsellAccept = () => {
    trackAddToCart();
    // Discounted bundle variant (matches the $25.20 price shown in the popup)
    window.location.href = 'https://noveliahub.com/cart/44198994378788:1';
  };
  const handleUpsellDecline = () => {
    trackAddToCart();
    window.location.href = CHECKOUT_URLS.base;
  };
  return <section id="pricing" className="py-20 md:py-32 bg-cream" data-id="element-275">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-276">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="bg-white rounded-[2.5rem] shadow-2xl overflow-hidden border border-sage-100" data-id="element-277">
          <div className="bg-sage-800 text-center py-4" data-id="element-278">
            <p className="text-sage-100 font-medium tracking-widest text-sm uppercase" data-id="element-279">
              Oferta especial por tiempo limitado
            </p>
          </div>

          <div className="p-8 md:p-16 text-center" data-id="element-280">
            <h2 className="text-3xl md:text-4xl font-bold mb-2" data-id="element-281">
              Comienza tu Reset Hoy
            </h2>
            <p className="text-charcoal-light mb-8" data-id="element-282">
              Acceso inmediato de por vida a los 15 módulos + 3 bonos
              exclusivos.
            </p>

            <div className="flex justify-center mb-8" data-id="element-283">
              <img src="https://cdn.magicpatterns.com/uploads/pCcM5Dc4cgTU6PvK7ojeSo/Mockup_Principal_Pagina.png" alt="Vuelve a Sentirte Tú - Programa completo con bonos" className="w-full max-w-md h-auto object-contain" data-id="element-284" />
            </div>

            <div className="flex flex-col items-center justify-center mb-10" data-id="element-285">
              <p className="text-xl text-gray-400 line-through mb-2 font-medium" data-id="element-286">
                Precio Real: $67.00 USD
              </p>
              <motion.div key={total} initial={{
              scale: 0.95,
              opacity: 0.6
            }} animate={{
              scale: 1,
              opacity: 1
            }} transition={{
              duration: 0.3,
              ease: 'easeOut'
            }} className="flex items-start justify-center text-sage-700" data-id="element-287">
                <span className="text-3xl font-bold mt-2" data-id="element-288">$</span>
                <span className="text-7xl md:text-8xl font-bold tracking-tighter" data-id="element-289">
                  {total}
                </span>
                <span className="text-xl font-bold mt-2 ml-1" data-id="element-290">USD</span>
              </motion.div>
            </div>

            {/* Primary CTA — above order bumps */}
            <Button onClick={handleCheckoutClick} className="w-full md:w-auto md:px-16 py-5 text-lg md:text-xl shadow-xl shadow-sage-700/20 mb-10" data-id="element-291">
              SÍ, QUIERO VOLVER A SENTIRME TÚ — ${total} USD
            </Button>

            {/* Order Bumps */}
            <div className="text-left mb-6" data-id="element-292">
              <p className="text-center text-xs uppercase tracking-widest text-sage-700 font-bold mb-4" data-id="element-293">
                ✨ Mejora tu pedido (Opcional)
              </p>
              <div className="space-y-4" data-id="element-294">
                <OrderBump image="https://cdn.magicpatterns.com/uploads/rxxpFw7NBE4Y3XMKuucTtt/Mockup_21_Desayunos.png" badge="OFERTA ÚNICA" title="21 Desayunos Hormonales en 5 Minutos" description="¿Sabías que el desayuno decide cómo funcionarán tus hormonas el resto del día? Añade este recetario exclusivo con 21 opciones rápidas, deliciosas y diseñadas para mantener tu azúcar estable, evitar antojos y darte energía hasta el almuerzo." regularPrice="$24" price="$9" checked={bumpBreakfast} onToggle={() => setBumpBreakfast(!bumpBreakfast)} data-id="element-295" />
                <OrderBump image="https://cdn.magicpatterns.com/uploads/d9A54kbHnBcUL5XoeZc6jP/recate_nocturno_final.png" badge="RESCATE NOCTURNO" title="Guía de Rescate Nocturno" description="No dejes que el estrés controle tus noches. Añade esta Guía de Rescate que incluye técnicas de respiración instantánea y una rutina de 3 minutos para calmar el sistema nervioso antes de dormir. Ideal para frenar sofocos nocturnos y silenciar la mente." price="$7" checked={bumpNight} onToggle={() => setBumpNight(!bumpNight)} data-id="element-296" />
              </div>
            </div>

            {/* Dynamic Cart Summary */}
            <div className="bg-sage-50 border border-sage-200 rounded-2xl p-5 md:p-6 text-left mb-8" data-id="element-297">
              <div className="flex items-center gap-2 mb-4 pb-3 border-b border-sage-200" data-id="element-298">
                <ShoppingCart size={18} className="text-sage-700" data-id="element-299" />
                <h4 className="font-semibold text-sage-800 text-sm md:text-base uppercase tracking-wider" data-id="element-300">
                  Resumen de tu pedido
                </h4>
              </div>

              <ul className="space-y-2 mb-4" data-id="element-301">
                <AnimatePresence initial={false} data-id="element-302">
                  {lineItems.filter(item => item.active).map(item => <motion.li key={item.label} initial={{
                  opacity: 0,
                  height: 0,
                  y: -8
                }} animate={{
                  opacity: 1,
                  height: 'auto',
                  y: 0
                }} exit={{
                  opacity: 0,
                  height: 0,
                  y: -8
                }} transition={{
                  duration: 0.25
                }} className="flex items-start justify-between gap-3 text-sm md:text-base text-charcoal" data-id="element-303">
                        <span className="flex-1 leading-snug" data-id="element-304">
                          {item.label}
                        </span>
                        <span className="font-semibold text-sage-800 flex-shrink-0" data-id="element-305">
                          ${item.price}.00
                        </span>
                      </motion.li>)}
                </AnimatePresence>
              </ul>

              <div className="flex items-center justify-between pt-3 border-t border-sage-200" data-id="element-306">
                <span className="font-bold text-charcoal text-base md:text-lg uppercase tracking-wider" data-id="element-307">
                  Total
                </span>
                <motion.span key={total} initial={{
                scale: 0.92,
                opacity: 0.5
              }} animate={{
                scale: 1,
                opacity: 1
              }} transition={{
                duration: 0.25
              }} className="text-2xl md:text-3xl font-bold text-sage-700" data-id="element-308">
                  ${total}.00 USD
                </motion.span>
              </div>
            </div>

            {/* Final dynamic checkout CTA */}
            <Button onClick={handleCheckoutClick} className="w-full md:w-auto md:px-16 py-5 text-lg md:text-xl shadow-xl shadow-sage-700/20 mb-6" data-id="element-309">
              Completar Compra — ${total} USD
            </Button>

            <div className="flex flex-col items-center justify-center gap-3 text-sm text-charcoal-light/70" data-id="element-310">
              <div className="flex items-center gap-2" data-id="element-311">
                <Lock size={16} data-id="element-312" />
                <span data-id="element-313">Pago 100% seguro y encriptado.</span>
              </div>
              <div className="flex items-center gap-2" data-id="element-314">
                <CreditCard size={16} data-id="element-315" />
                <span data-id="element-316">Recibirás acceso inmediato en tu email.</span>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Upsell popup — only fires if user clicks checkout without selecting any bump */}
      <UpsellPopup isOpen={showUpsell} onAccept={handleUpsellAccept} onDecline={handleUpsellDecline} onClose={() => setShowUpsell(false)} data-id="element-317" />
    </section>;
}